/**
 * Belegeinsichts-Termine (§ 18 Abs. 4 WEG)
 * ==========================================
 * Verwaltet Terminanfragen von Eigentümern zur Belegeinsicht.
 * Bestätigung wählt einen der vorgeschlagenen Slots.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Calendar, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS = {
  requested: { label: "Angefragt", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  confirmed: { label: "Bestätigt", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled: { label: "Storniert", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  completed: { label: "Durchgeführt", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  rejected:  { label: "Abgelehnt", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function BelegPruefungen() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/beleg-pruefungen");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!confirm("Termin wirklich stornieren?")) return;
    try {
      await api.post(`/beleg-pruefungen/${id}/cancel`);
      toast.success("Storniert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-4" data-testid="beleg-pruefungen-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Calendar size={24} className="text-emerald-700" />
          Belegeinsichts-Termine
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Terminanfragen von Eigentümern nach § 18 Abs. 4 WEG. Eigentümer schlagen
          Slots vor, du bestätigst einen. Bei Bestätigung wird der Eigentümer per E-Mail
          informiert.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Lade…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">
          Noch keine Terminanfragen.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Eigentümer</th>
                <th className="text-left px-3 py-2 w-40">Objekt</th>
                <th className="text-left px-3 py-2">Vorschläge / Termin</th>
                <th className="text-left px-3 py-2 w-32">Status</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const s = STATUS[r.status] || STATUS.requested;
                return (
                  <tr key={r.id} data-testid={`bp-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium">{r.owner_name}</td>
                    <td className="px-3 py-2 text-xs">{r.property_name || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.status === "confirmed" && r.confirmed_slot ? (
                        <div className="text-emerald-700 font-medium">
                          ✓ {fmtDT(r.confirmed_slot.from)} – {fmtDT(r.confirmed_slot.to)}
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          {(r.proposed_slots || []).map((sl, i) => (
                            <div key={i} className="text-slate-600">
                              • {fmtDT(sl.from)} – {fmtDT(sl.to)}
                            </div>
                          ))}
                          {r.notes && <div className="text-[10px] text-slate-500 mt-1 italic">„{r.notes}"</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${s.cls}`}>{s.label}</span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.status === "requested" && (
                        <div className="inline-flex gap-1">
                          <Button size="sm" className="h-7 text-xs gap-1 bg-emerald-700 hover:bg-emerald-800"
                                  onClick={() => setConfirming(r)}
                                  data-testid={`bp-confirm-${r.id}`}>
                            <CheckCircle size={11} /> Bestätigen
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600"
                                  onClick={() => cancel(r.id)} data-testid={`bp-cancel-${r.id}`}>
                            <XCircle size={12} />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirming && (
        <ConfirmDialog termin={confirming} onClose={() => setConfirming(null)}
                       onSaved={() => { setConfirming(null); load(); }} />
      )}
    </div>
  );
}

function ConfirmDialog({ termin, onClose, onSaved }) {
  const [slotIdx, setSlotIdx] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const slot = termin.proposed_slots[slotIdx];
    setSaving(true);
    try {
      await api.post(`/beleg-pruefungen/${termin.id}/confirm`, {
        confirmed_slot: slot, confirmation_notes: notes,
      });
      toast.success("Termin bestätigt · Eigentümer wird benachrichtigt");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="bp-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Termin bestätigen</DialogTitle>
          <DialogDescription>{termin.owner_name} · {termin.property_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-700 mb-1 block">Wähle einen Slot</label>
            <div className="space-y-1">
              {(termin.proposed_slots || []).map((s, i) => (
                <label key={i} className="flex items-center gap-2 cursor-pointer text-sm p-2 border border-slate-200 rounded hover:bg-slate-50">
                  <input type="radio" checked={slotIdx === i} onChange={() => setSlotIdx(i)}
                         data-testid={`bp-slot-${i}`} />
                  <span className="tabular-nums">{fmtDT(s.from)} – {fmtDT(s.to)}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-700 mb-1 block">Notiz für den Eigentümer</label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="z. B. Bitte melden Sie sich an der Rezeption"
                      data-testid="bp-confirm-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid="bp-confirm-submit">
            {saving ? "Bestätige…" : "Termin bestätigen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
